<template>
  <div
    class="c-invite-partner-search"
    :class="{ 'c-invite-partner-search--hide-tag': !hasAddOpenDataPartner }"
  >
    <BittsSelect
      ref="invitePartnerSearch"
      id="invite-partner-search"
      v-model="selectedCompany"
      :error="!!error"
      :error-message="error"
      :form-label="formLabel"
      placeholder="Search for a partner"
      :options="options"
      :open="dropdownOpen"
      option-type="company"
      dropdown-class-name="partner-search-dropdown bitts-select__dropdown"
      :dropdown-button="!!inviteButtonText"
      :allow-clear="true"
      :dynamic-options="true"
      @update:model-value="companySelected"
      @search-changed="onSearchChanged"
      @clear="clear"
    >
      <template #suffix="{ option }">
        <div class="c-invite-partner-search__domain">
          ({{ option.domain }})
        </div>
        <BittsTag
          v-if="option.public_invite_code && !isOffline"
          class="c-invite-partner-search__tag"
          color="success"
          text="on Crossbeam"
          size="x-small"
        />
      </template>
      <template #dropdownButtonBottom>
        <div class="flex justify-center px-4 mt-4 w-full">
          <BittsButton
            :text="inviteButtonText"
            size="x-small"
            class="w-full"
            type="neutral"
            :left-icon="['fak', 'add']"
            @click.prevent="onDropdownButtonClick"
          />
        </div>
      </template>
    </BittsSelect>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsSelect, BittsTag } from '@crossbeam/bitts';
import { Nullable } from '@crossbeam/types';

import { debounce } from 'lodash';
import { computed, ref } from 'vue';

import useClearbit from '@/composables/useClearbit';
import useHasOpenDataPartnerAccess from '@/composables/useHasOpenDataPartnerAccess';

export type Company = {
  name: Nullable<string>;
  domain: Nullable<string>;
  clearbit_domain?: Nullable<string>;
  public_invite_code: Nullable<string>;
  is_paid_customer?: Nullable<boolean>;
  value?: Nullable<string>;
  label?: Nullable<string>;
  logo?: Nullable<string>;
  uuid?: Nullable<string>;
};

const {
  error = null,
  isOffline,
  isOnboarding,
} = defineProps<{
  error?: Nullable<string>;
  isOffline?: boolean;
  isOnboarding?: boolean;
}>();

const emit = defineEmits<{
  (e: 'clear'): void;
  (e: 'input', value: { company: Company }): void;
}>();

const hasAddOpenDataPartner = useHasOpenDataPartnerAccess();

const { searchIcons } = useClearbit();

const invitePartnerSearch = ref();
const company = ref<Company | null>(null);
const options = ref<Company[]>([]);
const queryInProgress = ref('');
const selectedCompany = ref<string | undefined>();
const dropdownOpen = ref(false);

const inviteButtonText = computed((): string => {
  return queryInProgress.value.length
    ? customOptionLabel(queryInProgress.value)
    : '';
});

const formLabel = computed(() => {
  if (isOnboarding) return 'Invite a specific partner';
  return 'Select Partner';
});

const searchDebounced = debounce(async (query: string) => {
  options.value = await searchIcons(query);
}, 300);

function onSearchChanged(query: string | null) {
  queryInProgress.value = query || '';
  if (query) {
    dropdownOpen.value = true;
    searchDebounced(query);
  } else {
    dropdownOpen.value = false;
  }
}

function companySelected(val: Company | string | undefined) {
  if (!val) return;

  const companyResult =
    typeof val === 'string'
      ? options.value.find((org) => org.domain === val || org.uuid === val)
      : val;

  queryInProgress.value = '';
  dropdownOpen.value = false;
  selectedCompany.value = typeof val === 'string' ? val : val.name || undefined;
  emit('input', { company: companyResult || ({} as Company) });
}

function clear() {
  onSearchChanged(null);
  const emptyCompany: Company = {
    name: null,
    domain: null,
    public_invite_code: null,
  };
  companySelected(emptyCompany);
  selectedCompany.value = undefined;
  emit('clear');
}

function customOptionLabel(newCompanyName: string) {
  if (isOffline) return `Use ${newCompanyName}`;
  return `Invite "${newCompanyName}" to Crossbeam`;
}

function onDropdownButtonClick() {
  const newCompany: Company = {
    name: queryInProgress.value,
    value: queryInProgress.value,
    label: queryInProgress.value,
    domain: queryInProgress.value,
    logo: null,
    public_invite_code: null,
  };
  dropdownOpen.value = false;
  companySelected(newCompany);
  const activeDropdown = document.activeElement as HTMLElement;
  activeDropdown?.blur();
}
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.partner-search-dropdown {
  .bitts-select__option-label {
    @apply w-auto;
  }

  .c-invite-partner-search__domain {
    @apply flex-1 ml-4 text-neutral-text-weak;
  }
}
.c-invite-partner-search {
  .bitts-form-label__text {
    font-size: 14px;
  }

  .ant-select-selection-item {
    .c-invite-partner-search__domain {
      @apply hidden;
    }
  }

  &.c-invite-partner-search--hide-tag {
    .ant-select-selection-item {
      .c-invite-partner-search__tag {
        @apply hidden;
      }
    }
  }
}
</style>
